import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAssessment_companycoop, getAssessment_reportcoop, getAssessment_presentcoop, getAssessmentCoopByStId } from '../api/crud';
import { 
  Box, 
  Container, 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  TextField, 
  Button, 
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Drawer,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReactApexChart from 'react-apexcharts';
import InternStdDetail from '../Components/InternStdDetail';
import useMediaQuery from "@mui/material/useMediaQuery";

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
}));

const ScoreChip = styled(Chip)(({ score }) => {
  let color = 'default';
  let bgcolor = '#f5f5f5';
  
  if (score >= 90) {
    color = 'success';
    bgcolor = '#e8f5e8';
  } else if (score >= 80) {
    color = 'primary';
    bgcolor = '#e3f2fd';
  } else if (score >= 70) {
    color = 'warning';
    bgcolor = '#fff3e0';
  } else if (score >= 60) {
    color = 'error';
    bgcolor = '#ffebee';
  }
  
  return {
    backgroundColor: bgcolor,
    color: color === 'default' ? '#666' : undefined,
    fontWeight: 'medium',
  };
});

const CoopEvaluationDisplay = ({ reqId }) => {
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [studentSelected, setStudentSelected] = useState(null);
  const isMobile = useMediaQuery('(max-width:767px)');
  
  // State สำหรับการขยายข้อความ
  const [expandedSections, setExpandedSections] = useState({
    supervisor_comment: false,
    report_comment: false,
    present_comment: false,
    visit_comment: false
  });

  // ฟังก์ชันสำหรับ toggle การขยายข้อความ
  const toggleExpand = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // ฟังก์ชันสำหรับพิมพ์เป็น PDF
  const handlePrint = () => {
    const printStyles = `
      <style>
        body {
          margin: 0;
          padding: 0;
          background: #f5f5f5;
          font-family: 'TH Sarabun New', 'Sarabun', sans-serif;
          min-height: 100vh;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          padding: 20px 0;
        }
        
        .print-container {
          width: 210mm;
          min-height: 297mm;
          background: white;
          padding: 20mm;
          margin: 0 auto;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }
        
        @page {
          size: A4;
          margin: 0;
        }
        
        @media print {
          body {
            background: white;
            padding: 0;
          }
          
          .print-container {
            box-shadow: none;
            margin: 0;
            padding: 15mm;
            width: 100%;
            min-height: auto;
          }
          
          .no-print {
            display: none !important;
          }
          
          .page-break {
            page-break-before: always;
          }
        }
        
        h1, h2, h3, h4 {
          color: #1976d2;
          margin-top: 0;
        }
        
        .header-section {
          text-align: center;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 3px solid #1976d2;
        }
        
        .info-box {
          background: #f5f5f5;
          padding: 15px;
          margin: 15px 0;
          border-radius: 8px;
          border-left: 4px solid #2196f3;
        }
        
        .score-box {
          text-align: center;
          padding: 20px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 12px;
          margin: 20px 0;
        }
        
        .score-value {
          font-size: 48px;
          font-weight: bold;
          margin: 10px 0;
        }
        
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
        }
        
        th, td {
          padding: 12px;
          text-align: left;
          border: 1px solid #ddd;
        }
        
        th {
          background-color: #1976d2;
          color: white;
          font-weight: bold;
        }
        
        tr:nth-child(even) {
          background-color: #f9f9f9;
        }
      </style>
    `;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>ผลการประเมินสหกิจศึกษา - ${evaluation.req_id}</title>
        ${printStyles}
      </head>
      <body>
        <div class="print-container">
          <div class="header-section">
            <h1>ผลการประเมินการปฏิบัติงานสหกิจศึกษา</h1>
            <h3>คณะวิศวกรรมศาสตร์ มหาวิทยาลัยบูรพา</h3>
            <p>REQ ID: ${evaluation.req_id}</p>
          </div>

          <div class="info-box">
            <h3>ข้อมูลนิสิต</h3>
            <p><strong>รหัสนิสิต:</strong> ${getStudentId(evaluation.req_id)}</p>
            <p><strong>ชื่อ-นามสกุล:</strong> ${evaluation.student_name || '-'}</p>
            <p><strong>สาขาวิชา:</strong> ${evaluation.program || '-'}</p>
          </div>

          <div class="score-box">
            <h2 style="margin: 0 0 10px 0; color: white;">คะแนนรวม</h2>
            <div class="score-value">${evaluation.total_score || 0}</div>
            <p style="margin: 5px 0 0 0; font-size: 18px;">จากคะแนนเต็ม 100</p>
          </div>

          <table>
            <tr>
              <th>รายการประเมิน</th>
              <th style="text-align: center;">คะแนน</th>
              <th style="text-align: center;">คะแนนเต็ม</th>
              <th style="text-align: center;">เปอร์เซ็นต์</th>
            </tr>
            <tr>
              <td>คะแนนจากพี่เลี้ยง (Supervisor)</td>
              <td style="text-align: center;">${evaluation.supervisor_score || 0}</td>
              <td style="text-align: center;">${evaluation.supervisor_max || 200}</td>
              <td style="text-align: center;">40%</td>
            </tr>
            <tr>
              <td>คะแนนการนิเทศ (Visit)</td>
              <td style="text-align: center;">${evaluation.visit_score || 0}</td>
              <td style="text-align: center;">${evaluation.visit_max || 20}</td>
              <td style="text-align: center;">20%</td>
            </tr>
            <tr>
              <td>คะแนนการนำเสนอ (Presentation)</td>
              <td style="text-align: center;">${evaluation.present_score || 0}</td>
              <td style="text-align: center;">${evaluation.present_max || 30}</td>
              <td style="text-align: center;">30%</td>
            </tr>
            <tr>
              <td>คะแนนรายงาน (Report)</td>
              <td style="text-align: center;">${evaluation.report_score || 0}</td>
              <td style="text-align: center;">${evaluation.report_max || 100}</td>
              <td style="text-align: center;">10%</td>
            </tr>
            <tr style="background-color: #e3f2fd; font-weight: bold;">
              <td>คะแนนรวม</td>
              <td style="text-align: center;" colspan="3">${evaluation.total_score || 0}</td>
            </tr>
          </table>

          ${evaluation.supervisor_comment ? `
          <div class="info-box">
            <h3>ความคิดเห็นจากพี่เลี้ยง</h3>
            <p>${evaluation.supervisor_comment}</p>
          </div>
          ` : ''}

          ${evaluation.visit_comment ? `
          <div class="info-box">
            <h3>ความคิดเห็นจากอาจารย์นิเทศ</h3>
            <p>${evaluation.visit_comment}</p>
          </div>
          ` : ''}

          ${evaluation.present_comment ? `
          <div class="info-box">
            <h3>ความคิดเห็นจากการนำเสนอ</h3>
            <p>${evaluation.present_comment}</p>
          </div>
          ` : ''}

          ${evaluation.report_comment ? `
          <div class="info-box">
            <h3>ความคิดเห็นจากรายงาน</h3>
            <p>${evaluation.report_comment}</p>
          </div>
          ` : ''}

          <div style="margin-top: 40px; text-align: center; color: #666;">
            <p>พิมพ์เมื่อ: ${new Date().toLocaleDateString('th-TH', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</p>
          </div>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    
    printWindow.addEventListener('load', () => {
      setTimeout(() => {
        printWindow.print();
      }, 250);
    });
  };

  useEffect(() => {
    const fetchEvaluationData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all evaluation data for coop
        const [companyData, reportData, presentData, visitData] = await Promise.all([
          getAssessment_companycoop(),
          getAssessment_reportcoop(),
          getAssessment_presentcoop(),
          getAssessmentCoopByStId(reqId)
        ]);

        // Find matching records for this reqId
        const company = (companyData || []).find(item => item.req_id == reqId);
        const report = (reportData || []).find(item => item.req_id == reqId);
        const present = (presentData || []).find(item => item.req_id == reqId);
        
        // visitData might be a single object or an array, handle both cases
        let visit = null;
        if (Array.isArray(visitData)) {
          visit = visitData.find(item => item.req_id == reqId);
        } else if (visitData && visitData.req_id == reqId) {
          visit = visitData;
        }

        if (!company && !report && !present && !visit) {
          setError('ไม่พบข้อมูลการประเมินสำหรับ REQ ID นี้');
          setEvaluation(null);
          return;
        }

        // Calculate scores with percentages
        const supervisor_score = company?.total_score || 0;
        const supervisor_max = company?.max_score || 200;
        const supervisor_percentage = (parseFloat(supervisor_score) / parseFloat(supervisor_max)) * 40;

        const visit_score = visit?.total_score || 0;
        const visit_max = visit?.max_score || 20;
        const visit_percentage = (parseFloat(visit_score) / parseFloat(visit_max)) * 20;

        const present_score = present?.total_score || 0;
        const present_max = present?.max_score || 30;
        const present_percentage = (parseFloat(present_score) / parseFloat(present_max)) * 30;

        const report_score = report?.total_score || 0;
        const report_max = report?.max_score || 100;
        const report_percentage = (parseFloat(report_score) / parseFloat(report_max)) * 10;

        const total_score = supervisor_percentage + visit_percentage + present_percentage + report_percentage;

        // Combine all evaluation data
        const combinedEvaluation = {
          req_id: reqId,
          student_name: company?.student_name || visit?.student_name || '-',
          program: company?.program || visit?.program || '-',
          company: company?.company_name || '-',
          
          // Supervisor evaluation
          supervisor_score: supervisor_score,
          supervisor_max: supervisor_max,
          supervisor_percentage: supervisor_percentage.toFixed(2),
          supervisor_comment: company?.comment || company?.recommendations || '',
          
          // Visit evaluation
          visit_score: visit_score,
          visit_max: visit_max,
          visit_percentage: visit_percentage.toFixed(2),
          visit_comment: visit?.comment || '',
          
          // Presentation evaluation
          present_score: present_score,
          present_max: present_max,
          present_percentage: present_percentage.toFixed(2),
          present_comment: present?.comment || '',
          
          // Report evaluation
          report_score: report_score,
          report_max: report_max,
          report_percentage: report_percentage.toFixed(2),
          report_comment: report?.comment || '',
          
          // Total
          total_score: total_score.toFixed(2),
          
          // Additional info
          evaluator_name: company?.evaluator_name || company?.evaluator || '-',
          evaluation_date: company?.timestamp_text || company?.evaluation_date || '-'
        };

        setEvaluation(combinedEvaluation);
      } catch (err) {
        console.error('Error fetching evaluation data:', err);
        setError('เกิดข้อผิดพลาดในการโหลดข้อมูล กรุณาลองใหม่อีกครั้ง');
      } finally {
        setLoading(false);
      }
    };

    if (reqId) {
      fetchEvaluationData();
    }
  }, [reqId]);

  // ฟังก์ชันสำหรับแยกรหัสนิสิต 8 ตัวแรกจาก req_id
  const getStudentId = (reqId) => {
    return reqId ? reqId.toString().substring(0, 8) : '';
  };

  // ฟังก์ชันสำหรับสร้าง URL รูปนิสิต
  const getStudentImageUrl = (reqId) => {
    const studentId = getStudentId(reqId);
    return studentId ? `https://reg.buu.ac.th/registrar/getstudentimage.asp?id=${studentId}` : `${process.env.PUBLIC_URL}/assets/images/logo.png`;
  };

  // สร้างข้อมูลสำหรับ Radar Chart
  const getRadarChartData = () => {
    if (!evaluation) return { categories: [], series: [] };

    const categories = ['พี่เลี้ยง (40%)', 'การนิเทศ (20%)', 'การนำเสนอ (30%)', 'รายงาน (10%)'];
    const series = [{
      name: 'คะแนน',
      data: [
        parseFloat(evaluation.supervisor_percentage) || 0,
        parseFloat(evaluation.visit_percentage) || 0,
        parseFloat(evaluation.present_percentage) || 0,
        parseFloat(evaluation.report_percentage) || 0
      ]
    }];

    return { categories, series };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>กำลังโหลดข้อมูล...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mx: 'auto', maxWidth: 600 }}>
        {error}
      </Alert>
    );
  }

  if (!evaluation) {
    return (
      <Alert severity="info" sx={{ mx: 'auto', maxWidth: 600 }}>
        ไม่พบข้อมูลการประเมินสำหรับ REQ ID: {reqId}
      </Alert>
    );
  }

  return (
    <>
      {/* Drawer สำหรับแสดงรายละเอียดนิสิต */}
      <Drawer
        anchor="right"
        open={studentSelected ? true : false}
        onClose={() => {
          setStudentSelected(null);
        }}
      >
        <div
          className="card-body shadow-lg"
          style={{ width: "100vw", maxWidth: "600px" }}
        >
          <div className="row g-0">
            <div className="col-12 mb-4">
              <Box display="flex" justifyContent="flex-end">
                <Button
                  onClick={() => {
                    setStudentSelected(null);
                  }}
                  startIcon={<CloseIcon />}
                >
                  {isMobile ? 'Close' : ''}
                </Button>
              </Box>

              <InternStdDetail internRequestId={studentSelected?.req_id} />

              <Divider />
              {isMobile && (
                <Box display="flex" justifyContent="flex-start">
                  <Button
                    onClick={() => {
                      setStudentSelected(null);
                    }}
                    startIcon={<ArrowBackIcon />}
                  >
                    {isMobile ? 'BACK' : ''}
                  </Button>
                </Box>
              )}
            </div>
          </div>
          <hr />
        </div>
      </Drawer>

      <style jsx global>{`
        @media print {
          .no-print { display: none !important; }
          body { font-size: 12pt !important; }
        }
      `}</style>
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box 
          mb={4} 
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 3,
            p: 4,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="7" cy="7" r="1"/%3E%3Ccircle cx="27" cy="7" r="1"/%3E%3Ccircle cx="47" cy="7" r="1"/%3E%3Ccircle cx="7" cy="27" r="1"/%3E%3Ccircle cx="27" cy="27" r="1"/%3E%3Ccircle cx="47" cy="27" r="1"/%3E%3Ccircle cx="7" cy="47" r="1"/%3E%3Ccircle cx="27" cy="47" r="1"/%3E%3Ccircle cx="47" cy="47" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              pointerEvents: 'none'
            }
          }}
        >
          <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems="flex-start"
            sx={{ position: 'relative', zIndex: 1 }}
          >
            <Box sx={{ flex: 1 }}>
              <Box sx={{ mb: 2 }}>
                <Typography 
                  variant="h3" 
                  component="h1" 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: { xs: '1.8rem', md: '2.5rem' },
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    mb: 1,
                    color: 'white',
                    textAlign: 'left'
                  }}
                >
                  ผลการประเมินการปฏิบัติงานสหกิจศึกษา
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 'medium',
                    fontSize: '1.1rem',
                    mb: 2,
                    color: 'rgba(255, 255, 255, 0.9)',
                    textAlign: 'left'
                  }}
                >
                  คณะวิศวกรรมศาสตร์ มหาวิทยาลัยบูรพา
                </Typography>
              </Box>
              
              <Box 
                sx={{ 
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }}
              >
                <Typography 
                  variant="subtitle1" 
                  onClick={() => setStudentSelected(evaluation)}
                  role="button"
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    color: 'white',
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline',
                      transform: 'scale(1.02)',
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  REQ ID: {evaluation.req_id}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="พิมพ์รายงานเป็น PDF" arrow>
                <IconButton 
                  onClick={handlePrint}
                  size="large"
                  sx={{ 
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    '&:hover': { 
                      background: 'rgba(255, 255, 255, 0.3)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                    },
                    transition: 'all 0.3s ease',
                    width: 56,
                    height: 56
                  }}
                  className="no-print"
                >
                  <PrintIcon sx={{ fontSize: '24px' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* ข้อมูลพื้นฐาน */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={6}>
            <StyledCard>
              <CardContent sx={{ bgcolor: 'primary.50' }}>
                <Typography variant="h6" gutterBottom color="primary.main">
                  ข้อมูลนิสิต
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 0, pt: 1 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                      {evaluation.student_name}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          รหัสนิสิต:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {getStudentId(evaluation.req_id)}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          สาขาวิชา:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {evaluation.program}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          สถานประกอบการ:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {evaluation.company}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ flexShrink: 0, ml: 2, mr: 1 }}>
                    <img
                      src={getStudentImageUrl(evaluation.req_id)}
                      alt="รูปนิสิต"
                      onClick={() => setStudentSelected(evaluation)}
                      role="button"
                      style={{
                        width: '120px',
                        height: '150px',
                        objectFit: 'cover',
                        borderRadius: '12px',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.02)';
                        e.target.style.boxShadow = '0 6px 16px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                      }}
                      onError={(e) => {
                        e.target.src = '/assets/images/logo.png';
                      }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <StyledCard>
              <CardContent sx={{ bgcolor: 'success.50', height: '100%' }}>
                <Typography variant="h6" gutterBottom color="success.main">
                  ข้อมูลการประเมิน
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {evaluation.evaluator_name}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      วันที่ประเมิน:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
                      {evaluation.evaluation_date}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>

        {/* สรุปคะแนนรวม และ Radar Chart */}
        <StyledCard sx={{ mb: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* ส่วนซ้าย: สรุปคะแนนรวม */}
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  p: 2.5,
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <Typography variant="h6" gutterBottom sx={{ 
                    color: 'primary.main', 
                    fontWeight: 'bold',
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '1.1rem'
                  }}>
                    สรุปคะแนนรวม
                  </Typography>
                  
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Box 
                        sx={{ 
                          textAlign: 'center',
                          p: 1.5,
                          bgcolor: 'white',
                          borderRadius: 2,
                          border: '2px solid #667eea',
                          boxShadow: '0 3px 10px rgba(102, 126, 234, 0.2)',
                          position: 'relative',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '3px',
                            bgcolor: '#667eea',
                            borderRadius: '4px 4px 0 0'
                          }
                        }}
                      >
                        <Typography variant="h2" sx={{ color: '#667eea', fontWeight: 'bold', mb: 0.5, fontSize: '2.8rem' }}>
                          {evaluation.total_score}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#667eea', fontSize: '1rem', lineHeight: 1.2 }}>
                          คะแนนรวม<br/><span style={{ fontSize: '0.85rem', opacity: 0.8, fontWeight: 'normal' }}>(เต็ม 100)</span>
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Box 
                        sx={{ 
                          textAlign: 'center',
                          p: 1.5,
                          bgcolor: 'white',
                          borderRadius: 2,
                          border: '2px solid #2196f3',
                          position: 'relative',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '3px',
                            bgcolor: '#2196f3',
                            borderRadius: '4px 4px 0 0'
                          }
                        }}
                      >
                        <Typography variant="h4" sx={{ color: '#2196f3', fontWeight: 'bold', mb: 0.5, fontSize: '1.8rem' }}>
                          {evaluation.supervisor_percentage}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.75rem', lineHeight: 1.2 }}>
                          พี่เลี้ยง<br/><span style={{ fontSize: '0.7rem', opacity: 0.8 }}>(40%)</span>
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Box 
                        sx={{ 
                          textAlign: 'center',
                          p: 1.5,
                          bgcolor: 'white',
                          borderRadius: 2,
                          border: '2px solid #4caf50',
                          position: 'relative',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '3px',
                            bgcolor: '#4caf50',
                            borderRadius: '4px 4px 0 0'
                          }
                        }}
                      >
                        <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 'bold', mb: 0.5, fontSize: '1.8rem' }}>
                          {evaluation.visit_percentage}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.75rem', lineHeight: 1.2 }}>
                          การนิเทศ<br/><span style={{ fontSize: '0.7rem', opacity: 0.8 }}>(20%)</span>
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6}>
                      <Box 
                        sx={{ 
                          textAlign: 'center',
                          p: 1.5,
                          bgcolor: 'white',
                          borderRadius: 2,
                          border: '2px solid #ff9800',
                          position: 'relative',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '3px',
                            bgcolor: '#ff9800',
                            borderRadius: '4px 4px 0 0'
                          }
                        }}
                      >
                        <Typography variant="h4" sx={{ color: '#ff9800', fontWeight: 'bold', mb: 0.5, fontSize: '1.8rem' }}>
                          {evaluation.present_percentage}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.75rem', lineHeight: 1.2 }}>
                          การนำเสนอ<br/><span style={{ fontSize: '0.7rem', opacity: 0.8 }}>(30%)</span>
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6}>
                      <Box 
                        sx={{ 
                          textAlign: 'center',
                          p: 1.5,
                          bgcolor: 'white',
                          borderRadius: 2,
                          border: '2px solid #9c27b0',
                          position: 'relative',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '3px',
                            bgcolor: '#9c27b0',
                            borderRadius: '4px 4px 0 0'
                          }
                        }}
                      >
                        <Typography variant="h4" sx={{ color: '#9c27b0', fontWeight: 'bold', mb: 0.5, fontSize: '1.8rem' }}>
                          {evaluation.report_percentage}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.75rem', lineHeight: 1.2 }}>
                          รายงาน<br/><span style={{ fontSize: '0.7rem', opacity: 0.8 }}>(10%)</span>
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* ส่วนขวา: Radar Chart */}
              <Grid item xs={12} md={6}>
                <Box sx={{ height: '100%', minHeight: '400px' }}>
                  <Typography variant="h6" gutterBottom sx={{ 
                    color: 'primary.main', 
                    fontWeight: 'bold',
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '1.1rem'
                  }}>
                    การแสดงผลการประเมินตามส่วนประกอบ
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.85rem' }}>
                    แสดงคะแนนแยกตามส่วนประกอบการประเมิน 4 ด้าน
                  </Typography>
                  
                  <Box sx={{ height: '320px', mb: 2 }}>
                    <ReactApexChart
                      options={{
                        chart: {
                          type: 'radar',
                          toolbar: {
                            show: false
                          }
                        },
                        xaxis: {
                          categories: getRadarChartData().categories,
                          labels: {
                            style: {
                              fontSize: '11px',
                              fontFamily: 'Sarabun, sans-serif'
                            }
                          }
                        },
                        yaxis: {
                          show: true,
                          min: 0,
                          max: 40,
                          tickAmount: 4,
                          labels: {
                            formatter: function (val) {
                              return val.toFixed(0);
                            }
                          }
                        },
                        plotOptions: {
                          radar: {
                            size: 120,
                            polygons: {
                              strokeColors: '#e9e9e9',
                              fill: {
                                colors: ['#f8f9fa', '#ffffff']
                              }
                            }
                          }
                        },
                        colors: ['#667eea'],
                        markers: {
                          size: 5,
                          colors: ['#667eea'],
                          strokeColor: '#fff',
                          strokeWidth: 2
                        },
                        fill: {
                          opacity: 0.1
                        },
                        stroke: {
                          width: 2
                        },
                        legend: {
                          show: false
                        },
                        title: {
                          text: 'ผลการประเมินตามส่วนประกอบ',
                          align: 'center',
                          style: {
                            fontSize: '14px',
                            fontFamily: 'Sarabun, sans-serif',
                            fontWeight: 600
                          }
                        }
                      }}
                      series={getRadarChartData().series}
                      type="radar"
                      height={320}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </StyledCard>

        {/* ข้อมูลเพิ่มเติม */}
        <StyledCard sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
              ข้อมูลเพิ่มเติมจากการประเมิน
            </Typography>
            
            <Grid container spacing={3}>
              {evaluation.supervisor_comment && (
                <Grid item xs={12} md={6}>
                  <Box 
                    sx={{ 
                      p: 3, 
                      border: '1px solid #e8f5e8',
                      borderLeft: '4px solid #4caf50',
                      borderRadius: 2,
                      bgcolor: '#f8fff8',
                      height: '100%',
                      minHeight: '180px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                        ความคิดเห็นจากพี่เลี้ยง
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ 
                        flex: 1,
                        overflow: 'hidden',
                        position: 'relative'
                      }}
                    >
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          lineHeight: 1.6,
                          maxHeight: evaluation.supervisor_comment.length > 200 && !expandedSections.supervisor_comment ? '120px' : 'auto',
                          overflow: evaluation.supervisor_comment.length > 200 && !expandedSections.supervisor_comment ? 'hidden' : 'visible',
                          display: evaluation.supervisor_comment.length > 200 && !expandedSections.supervisor_comment ? '-webkit-box' : 'block',
                          WebkitLineClamp: evaluation.supervisor_comment.length > 200 && !expandedSections.supervisor_comment ? 5 : 'none',
                          WebkitBoxOrient: 'vertical',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {evaluation.supervisor_comment}
                      </Typography>
                      {evaluation.supervisor_comment.length > 200 && (
                        <Box 
                          sx={{ 
                            mt: 1,
                            textAlign: 'right'
                          }}
                        >
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#2e7d32', 
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => toggleExpand('supervisor_comment')}
                          >
                            {expandedSections.supervisor_comment ? 'แสดงน้อยลง' : '...อ่านเพิ่ม'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}

              {evaluation.visit_comment && (
                <Grid item xs={12} md={6}>
                  <Box 
                    sx={{ 
                      p: 3, 
                      border: '1px solid #e3f2fd',
                      borderLeft: '4px solid #2196f3',
                      borderRadius: 2,
                      bgcolor: '#f8fbff',
                      height: '100%',
                      minHeight: '180px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        ความคิดเห็นจากอาจารย์นิเทศ
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ 
                        flex: 1,
                        overflow: 'hidden',
                        position: 'relative'
                      }}
                    >
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          lineHeight: 1.6,
                          maxHeight: evaluation.visit_comment.length > 200 && !expandedSections.visit_comment ? '120px' : 'auto',
                          overflow: evaluation.visit_comment.length > 200 && !expandedSections.visit_comment ? 'hidden' : 'visible',
                          display: evaluation.visit_comment.length > 200 && !expandedSections.visit_comment ? '-webkit-box' : 'block',
                          WebkitLineClamp: evaluation.visit_comment.length > 200 && !expandedSections.visit_comment ? 5 : 'none',
                          WebkitBoxOrient: 'vertical',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {evaluation.visit_comment}
                      </Typography>
                      {evaluation.visit_comment.length > 200 && (
                        <Box 
                          sx={{ 
                            mt: 1,
                            textAlign: 'right'
                          }}
                        >
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#1976d2', 
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => toggleExpand('visit_comment')}
                          >
                            {expandedSections.visit_comment ? 'แสดงน้อยลง' : '...อ่านเพิ่ม'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}

              {evaluation.present_comment && (
                <Grid item xs={12} md={6}>
                  <Box 
                    sx={{ 
                      p: 3, 
                      border: '1px solid #fff3e0',
                      borderLeft: '4px solid #ff9800',
                      borderRadius: 2,
                      bgcolor: '#fffcf8',
                      height: '100%',
                      minHeight: '180px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                        ความคิดเห็นจากการนำเสนอ
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ 
                        flex: 1,
                        overflow: 'hidden',
                        position: 'relative'
                      }}
                    >
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          lineHeight: 1.6,
                          maxHeight: evaluation.present_comment.length > 200 && !expandedSections.present_comment ? '120px' : 'auto',
                          overflow: evaluation.present_comment.length > 200 && !expandedSections.present_comment ? 'hidden' : 'visible',
                          display: evaluation.present_comment.length > 200 && !expandedSections.present_comment ? '-webkit-box' : 'block',
                          WebkitLineClamp: evaluation.present_comment.length > 200 && !expandedSections.present_comment ? 5 : 'none',
                          WebkitBoxOrient: 'vertical',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {evaluation.present_comment}
                      </Typography>
                      {evaluation.present_comment.length > 200 && (
                        <Box 
                          sx={{ 
                            mt: 1,
                            textAlign: 'right'
                          }}
                        >
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#f57c00', 
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => toggleExpand('present_comment')}
                          >
                            {expandedSections.present_comment ? 'แสดงน้อยลง' : '...อ่านเพิ่ม'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}

              {evaluation.report_comment && (
                <Grid item xs={12} md={6}>
                  <Box 
                    sx={{ 
                      p: 3, 
                      border: '1px solid #f3e5f5',
                      borderLeft: '4px solid #9c27b0',
                      borderRadius: 2,
                      bgcolor: '#fdfaff',
                      height: '100%',
                      minHeight: '180px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>
                        ความคิดเห็นจากรายงาน
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ 
                        flex: 1,
                        overflow: 'hidden',
                        position: 'relative'
                      }}
                    >
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          lineHeight: 1.6,
                          maxHeight: evaluation.report_comment.length > 200 && !expandedSections.report_comment ? '120px' : 'auto',
                          overflow: evaluation.report_comment.length > 200 && !expandedSections.report_comment ? 'hidden' : 'visible',
                          display: evaluation.report_comment.length > 200 && !expandedSections.report_comment ? '-webkit-box' : 'block',
                          WebkitLineClamp: evaluation.report_comment.length > 200 && !expandedSections.report_comment ? 5 : 'none',
                          WebkitBoxOrient: 'vertical',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {evaluation.report_comment}
                      </Typography>
                      {evaluation.report_comment.length > 200 && (
                        <Box 
                          sx={{ 
                            mt: 1,
                            textAlign: 'right'
                          }}
                        >
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#7b1fa2', 
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => toggleExpand('report_comment')}
                          >
                            {expandedSections.report_comment ? 'แสดงน้อยลง' : '...อ่านเพิ่ม'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </StyledCard>
      </Container>
    </>
  );
};

// Component สำหรับใช้กับ Router
const CoopEvaluationPage = () => {
  const [searchParams] = useSearchParams();
  const reqIdFromUrl = searchParams.get('req_id');
  const [reqId, setReqId] = useState(reqIdFromUrl || '');
  const [showResults, setShowResults] = useState(!!reqIdFromUrl);

  useEffect(() => {
    if (reqIdFromUrl) {
      setReqId(reqIdFromUrl);
      setShowResults(true);
    }
  }, [reqIdFromUrl]);

  const handleSearch = () => {
    if (reqId.trim()) {
      setShowResults(true);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Search Section */}
        <StyledCard sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              ค้นหาผลการประเมินสหกิจศึกษา
            </Typography>
            <Box display="flex" gap={2} alignItems="end">
              <TextField
                fullWidth
                label="REQ ID"
                value={reqId}
                onChange={(e) => setReqId(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="กรอก REQ ID เช่น 6405021601"
                variant="outlined"
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                sx={{ minWidth: '120px', height: '56px' }}
              >
                ค้นหา
              </Button>
            </Box>
          </CardContent>
        </StyledCard>

        {/* Results */}
        {showResults && reqId && (
          <>
            <CoopEvaluationDisplay reqId={reqId} />
          </>
        )}
      </Container>
    </Box>
  );
};

export default CoopEvaluationPage;
